import React, { useEffect, useRef, useState } from 'react';
import { BiChevronLeft, BiChevronRight } from 'react-icons/bi';
import { twMerge } from 'tailwind-merge';

import Left from '@/components/HomePage/Banners/CustomArrow/left';
import Right from '@/components/HomePage/Banners/CustomArrow/right';

interface Props {
  children: React.ReactNode;
  scrollAmount?: number;
  className?: string;
  classNameRoot?: string;
  buttonClass?: {
    left?: string;
    leftIcon?: string;
    right?: string;
    rightIcon?: string;
  };
  arrowVarient?: 'default' | 'vertical';
}

const HorizontalScroll: React.FC<Props> = ({
  children,
  scrollAmount = 300,
  className,
  classNameRoot,
  buttonClass,
  arrowVarient = 'default',
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(true);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;

    const amount = direction === 'left' ? -scrollAmount : scrollAmount;

    scrollRef.current.scrollBy({
      left: amount,
      behavior: 'smooth',
    });
  };

  const CustomRightArrow = ({ onClick }: { onClick?: () => void }) => {
    return (
      <div className='absolute right-[6px] top-0 h-full z-20 flex items-center'>
        <button
          onClick={onClick}
          className={twMerge(
            'w-7 h-7 rounded-full bg-white/40 backdrop-blur-[35px] flex items-center justify-center',
            buttonClass?.right,
          )}
          aria-label='Next'
        >
          <BiChevronRight
            className={twMerge('text-black w-6 h-6', buttonClass?.rightIcon)}
          />
        </button>
      </div>
    );
  };

  const CustomLeftArrow = ({ onClick }: { onClick?: () => void }) => {
    return (
      <div className='absolute left-[6px] top-0 h-full z-20 flex items-center'>
        <button
          onClick={onClick}
          className={twMerge(
            'w-7 h-7 rounded-full bg-white/40 backdrop-blur-[35px] flex items-center justify-center',
            buttonClass?.left,
          )}
          aria-label='Prev'
        >
          <BiChevronLeft
            className={twMerge('text-black w-6 h-6', buttonClass?.leftIcon)}
          />
        </button>
      </div>
    );
  };

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;

    setShowLeft(scrollLeft > 0);
    setShowRight(scrollLeft + clientWidth < scrollWidth - 1);
  };

  useEffect(() => {
    updateArrows();
  }, [children]);

  return (
    <div className={twMerge('relative', classNameRoot)}>
      {showLeft &&
        (arrowVarient === 'vertical' ? (
          <Left
            className='lg:left-[40px]'
            gradientClass='-left-[40px]'
            onClick={() => scroll('left')}
          />
        ) : (
          <CustomLeftArrow onClick={() => scroll('left')} />
        ))}
      {showRight &&
        (arrowVarient === 'vertical' ? (
          <Right
            className='lg:right-[40px]'
            gradientClass='-right-[40px]'
            onClick={() => scroll('right')}
          />
        ) : (
          <CustomRightArrow onClick={() => scroll('right')} />
        ))}

      <div
        ref={scrollRef}
        onScroll={updateArrows}
        className={twMerge(
          'flex overflow-x-auto scroll-smooth no-scrollbar',
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
};

export default HorizontalScroll;
